import React, { useLayoutEffect, useRef, useState } from "react"
import { Person } from "@mui/icons-material"
import styled, { css } from "styled-components"
import { formatPatientName } from "../../formatters"
import { theme } from "../../../theme"
import { TextBold, TextRegular, TextLink } from "../../atoms/text"

export type ShortPatientProfileProps = {
  patient: ShortProfileInfo
  className?: string
  onClickPatient?: (patientId?: string) => void
  row?: boolean
  showIcon?: boolean
  nameBold?: boolean
  dataCy?: string
}

export type ShortProfileInfo = {
  id?: string
  firstName?: string
  lastName?: string
  middleName?: string
  birthDate?: string
  customPatientId?: string
}

export const ShortPatientProfile = (props: ShortPatientProfileProps) => {
  const { patient, className, onClickPatient, row = false, showIcon = true, nameBold = false, dataCy } = props
  const birthDateRef = useRef<HTMLSpanElement>(null)
  const patientIdRef = useRef<HTMLSpanElement>(null)

  const [birthDateWidth, setBirthDateWidth] = useState(0)
  const [patientIdWidth, setPatientIdWidth] = useState(0)

  useLayoutEffect(() => {
    setBirthDateWidth(birthDateRef.current?.offsetWidth ?? 0)
    setPatientIdWidth(patientIdRef.current?.offsetWidth ?? 0)
  }, [])

  const renderPatientName = () => {
    if (!patient.firstName && !patient.lastName) {
      return <NoPatientName>-</NoPatientName>
    }

    if (!!onClickPatient) {
      return (
        <PatientLink
          onClick={() => !!onClickPatient && onClickPatient(patient.id)}
          className={className}
          $birthDateWidth={birthDateWidth}
          $patientIdWidth={patientIdWidth}
        >
          <PatientName noWrap>{formatPatientName(patient.firstName, patient.lastName, patient.middleName)}</PatientName>
        </PatientLink>
      )
    }

    return (
      <TextWrap $birthDateWidth={birthDateWidth} $patientIdWidth={patientIdWidth}>
        {nameBold && (
          <TextBold noWrap>{formatPatientName(patient.firstName, patient.lastName, patient.middleName)}</TextBold>
        )}

        {!nameBold && (
          <TextRegular noWrap>{formatPatientName(patient.firstName, patient.lastName, patient.middleName)}</TextRegular>
        )}
      </TextWrap>
    )
  }

  const renderCustomPatientId = () => {
    if (row) {
      return (
        patient.customPatientId && (
          <CustomPatientId ref={patientIdRef}>{`· ${patient.customPatientId || ""}`}</CustomPatientId>
        )
      )
    }
    return <TextRegular noWrap>{patient.customPatientId || ""}</TextRegular>
  }

  return (
    <Root className={className} data-cy={dataCy}>
      {showIcon && <PatientIcon />}
      <PatientNameId $row={row}>
        <FirstRow>
          {renderPatientName()}
          {!!patient.birthDate && <Birthdate ref={birthDateRef}>{`· ${patient.birthDate}`}</Birthdate>}
          {row && renderCustomPatientId()}
        </FirstRow>
        {!row && renderCustomPatientId()}
      </PatientNameId>
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  gap: ${theme.spacing.m};
`

const NoPatientName = styled.span`
  line-height: ${theme.lineHeight.m};
`

const FirstRow = styled.div`
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.s};
  align-items: center;
`

const PatientIcon = styled(Person)`
  color: ${theme.color.grey3};
  width: ${theme.iconSize.xs};
  height: ${theme.iconSize.xs};
  margin-top: ${theme.spacing.s};
`

const PatientNameId = styled.div<{ $row: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.$row === true ? "row" : "column")};
  overflow: hidden;
  width: 100%;
  align-items: ${(p) => (p.$row === true ? "center" : "flex-start")};
  gap: ${theme.spacing.s};
  vertical-align: middle;
`

const PatientName = styled(TextBold)`
  color: ${theme.color.primary};
`

const PatientLink = styled(TextLink)<{ $birthDateWidth?: number; $patientIdWidth?: number }>`
  display: inline-flex;
  // Basically take all the space it can after deducing the birthdate and patientId if they are sharing the same line
  max-width: ${(p) => css`calc(100% - ${p.$birthDateWidth}px - ${p.$patientIdWidth}px)`};
  min-width: 50px;
  z-index: 10;
`

const TextWrap = styled.span<{ $birthDateWidth?: number; $patientIdWidth?: number }>`
  display: inline-flex;
  max-width: ${(p) => css`calc(100% - ${p.$birthDateWidth}px - ${p.$patientIdWidth}px)`};
  min-width: 50px;
`

const Birthdate = styled.span`
  color: ${theme.color.black4};
  white-space: nowrap;
`

const CustomPatientId = styled.span`
  color: ${theme.color.black4};
  white-space: nowrap;
`
